// What the till's order store says of itself (#382; GET /store/state): whether
// it reaches Bosla, how many closed sales Bosla has not confirmed yet, how
// many open orders it holds, and whether the owner removed the till from its
// shop. The screen reads it every 2 seconds, from the lock screens on, and
// after its own writes (N14); every rule behind it is the store's.

import { store } from '../api/client';
import type { components } from '../api/store';
import { fill, message, type Language, type MessageKey } from '../i18n/messages';

export type StoreState = components['schemas']['StoreState'];

/** How often the till asks its store (N14). */
export const STATE_POLL_MS = 2000;

/**
 * How long the waiting count must stay above 0 before the till shows it, so a
 * sale that reaches Bosla at once never flashes a count (N14).
 */
export const WAITING_SHOWN_AFTER_MS = 5000;

let state = $state<StoreState | null>(null);
// When the count last went above 0, or null while it is 0.
let waitingSince: number | null = null;
let now = $state(0);

/** The store's last answer, or null before the first. */
export function storeState(): StoreState | null {
	return state;
}

/**
 * Whether the store reaches Bosla (N15). True until the store says otherwise:
 * a store that does not answer at all is the till's own offline notice.
 */
export function linkUp(): boolean {
	return state?.online ?? true;
}

/** The waiting count the till shows: 0 until it has been above 0 for 5 seconds. */
export function shownWaiting(): number {
	return waitingShown(state?.waiting ?? 0, waitingSince, now);
}

/**
 * The count to show, from the count, when it went above 0 and now: the
 * count once it has stayed above 0 for WAITING_SHOWN_AFTER_MS, else 0.
 */
export function waitingShown(count: number, since: number | null, at: number): number {
	if (count <= 0 || since === null || at - since < WAITING_SHOWN_AFTER_MS) return 0;
	return count;
}

/** The removed till's figures (T21), or null while the till is not removed. */
export function removal(): { waiting: number; openOrders: number } | null {
	if (state?.stoppedBy !== 'revoked') return null;
	return { waiting: state.waiting, openOrders: state.openOrders ?? 0 };
}

/** Take one answer of the store's, at the time given. */
export function takeState(next: StoreState, at: number): void {
	if (next.waiting > 0) {
		waitingSince ??= at;
	} else {
		waitingSince = null;
	}
	state = next;
	now = at;
}

// The turn of the last read asked, and of the last answer taken: an answer to
// a read older than one already taken is dropped, so a poll that left before
// a write never puts back what the write changed, such as a shift opened.
let asked = 0;
let taken = 0;

/** Ask the store once. A store that does not answer leaves what the till knew. */
export async function readStoreState(): Promise<void> {
	const turn = ++asked;
	try {
		const { data, error } = await store.GET('/store/state');
		if (error !== undefined || !data || turn < taken) return;
		taken = turn;
		takeState(data, Date.now());
	} catch {
		// The store did not answer: the till's own offline notice says so.
	}
}

/** Ask the store now and every STATE_POLL_MS. Returns the stop. */
export function watchStoreState(): () => void {
	void readStoreState();
	const tick = setInterval(() => void readStoreState(), STATE_POLL_MS);
	return () => clearInterval(tick);
}

/** What the store answered the screen's idle lock (N18). */
export type IdleLock = 'locked' | 'held' | 'unknown';

/**
 * Ask the store before an idle lock. It locks the cashier's session and
 * answers 204 while it reaches Bosla, and holds the lock open with 409
 * IDLE_LOCK_HELD while it does not, because unlocking again needs Bosla.
 * Any other answer, or none, is unknown, and the screen locks as it did.
 */
export async function askIdleLock(): Promise<IdleLock> {
	try {
		const { error, response } = await store.POST('/store/idle-lock');
		if (response.status === 204) return 'locked';
		const code = (error as { code?: string } | undefined)?.code;
		if (response.status === 409 && code === 'IDLE_LOCK_HELD') return 'held';
		return 'unknown';
	} catch {
		return 'unknown';
	}
}

/**
 * A line that names a count: its own words for one and for two, which Arabic
 * says in a form of their own, and the count put in for more.
 */
export function countLine(
	language: Language,
	count: number,
	one: MessageKey,
	two: MessageKey,
	many: MessageKey,
): string {
	if (count === 1) return message(language, one);
	if (count === 2) return message(language, two);
	return fill(message(language, many), count);
}

export type ConnectionLine =
	| { kind: 'storeDown' }
	| { kind: 'netDown'; text: string }
	| { kind: 'waiting'; text: string }
	| { kind: 'none' };

/** T3's standing line, from whether the store answers, its link and waiting count. */
export function connectionLine(
	language: Language,
	storeAnswers: boolean,
	link: boolean,
	waiting: number,
): ConnectionLine {
	if (!storeAnswers) return { kind: 'storeDown' };
	if (!link) {
		return {
			kind: 'netDown',
			text:
				waiting === 0
					? message(language, 'tillNetDown')
					: countLine(language, waiting, 'tillNetDownOne', 'tillNetDownTwo', 'tillNetDownMany'),
		};
	}
	if (waiting > 0) {
		return {
			kind: 'waiting',
			text: countLine(language, waiting, 'tillWaitingOne', 'tillWaitingTwo', 'tillWaitingMany'),
		};
	}
	return { kind: 'none' };
}

/** Forget what the store said, for a test. */
export function resetStoreState(): void {
	state = null;
	waitingSince = null;
	now = 0;
	asked = 0;
	taken = 0;
}
